<template>
    <div class="doctorMenuWrapper">
        <div v-if="isDraft" class="headLabelWrapper align-items-center">
            <div class="headLabel headLabelDraft mr-2">{{ `予約未完了の${$locale.consultation_host}` }}</div>
            <div class="headLabel headLabelCancel">前回キャンセル</div>
        </div>
        <div v-else-if="isDraftByDoctor">
            <div class="headLabel headLabelDraftByDoctor">{{ `医師が追加した${$locale.consultation_host}予約` }}</div>
        </div>
        <div v-else-if="withRepeatableDate && lastRepeatReservableDate !== null">
            <div class="headLabel headLabelRepeat">{{ lastRepeatReservableDate.format(`M月D日まで再${$locale.consultation_host}受付中`) }}</div>
        </div>

        <div class="doctorMenuMedicalCourseName">{{ menu.medical_course.name }}</div>
        <div
            v-if="menu.is_health_package"
            class="doctorMenuHealthPackageIcon">
            <img src="/images/common/label_health_package1.png">
        </div>
        <div class="doctorMenuTitle">{{ menu.title }}</div>

        <div class="doctorMenuConsultationOverview d-block d-md-flex">
            <div class="doctorMenuConsultationMinutes">
                <span>{{ $locale.consultation_host }}時間:</span>
                <span class="large">{{ menu.consultation_minutes }}</span><!--
             --><span>分</span>
            </div>
            <div
                v-if="isPayPrice"
                class="doctorMenuConsultationPrice d-flex">
                <div>
                    <span>&nbsp;|&nbsp;</span><!--
                 --><span>{{ $locale.consultation_host }}料:</span><!--
                 --><span :class="{ 'discounted' : hasDiscount }" class="large">
                        {{ helper.inTax(discountedPrice).toLocaleString() }}
                    </span><!--
                 --><span :class="{ 'discounted' : hasDiscount }">円</span>
                </div>
                <div
                    v-if="hasDiscount"
                    class="doctorMenuConsultationPriceOrigin">
                    <span>{{ helper.inTax(menu.consultation_price).toLocaleString() }}</span><!--
                 --><span>円</span>
                </div>
            </div>
        </div>

        <div
            v-if="$locale.type === 'consul'"
            class="doctorMenuConsultationOverviewInfo">
            <div class="doctorMenuConsultationOverviewInfoCoupon">
                <div v-if="isDiscountViaRepeat && maxDiscountCoupon !== null">
                    ※再{{ $locale.consultation_host }}価格が適用されます（クーポン併用可能）
                </div>
                <div v-else-if="isDiscountViaRepeat && maxDiscountCoupon === null">
                    ※再{{ $locale.consultation_host }}価格が適用されます
                </div>
                <div v-else-if="!isDiscountViaRepeat && maxDiscountCoupon !== null">
                    <span v-if="userType === 'user'">※お持ちのクーポンが使用できます</span>
                    <span v-else>※クーポンが使用できます</span>
                </div>
            </div>
            <div v-if="menu.is_health_package">
                ※&nbsp;2ヶ月に1回まで無料で{{ $locale.consultation_host }}が可能です
            </div>
        </div>
        <div class="mt-2 pt-1">
            <div
                v-for="label in menu.doctor_menu_labels"
                class="doctorMenuLabel">
                {{ label.name }}
            </div>
        </div>

        <template v-if="withDescription && menu.description !== null">
            <div
                class="descriptionWrapper mt-1"
                :class="{ 'descriptionWrapperExtended' : isShowDoctorMenuDescription }">
                <div class="description">{{ menu.description }}</div>
            </div>
            <div
                v-if="withExtender && isShowDoctorMenuDescription === false"
                @click="isShowDoctorMenuDescription = true"
                class="descriptionExtender">
                ▼&nbsp;全文を表示
            </div>
        </template>
    </div>
</template>

<script>
    import YokumiruHelper from '../../YokumiruHelper'

    export default {
        props: {
            menu: {
                type: Object,
                required: true,
            },
            user: {
                type: Object|null,
                default: null,
            },
            userType: {
                type: String,
                default: 'user',
            },
            userCoupons: {
                type: Array,
                default: () => { return [] },
            },
            consultation: {
                type: Object,
                default: null,
            },
            staticSubtotal: {
                type: Number|null,
                default: null,
            },
            withExtender: {
                type: Boolean,
                default: false,
            },
            withDescription: {
                type: Boolean,
                default: false,
            },
            withRepeatableDate: {
                type: Boolean,
                default: false,
            },
            isDraft: {
                type: Boolean,
                default: false,
            },
            isDraftByDoctor: {
                type: Boolean,
                default: false,
            },
        },

        data: function () {
            return {
                helper: YokumiruHelper,
                isShowDoctorMenuDescription: true,
            }
        },

        computed: {
            /**
             * 相談料の決済が必要かどうか
             * @return {boolean}
             */
            isPayPrice: function () {
                if (this.$locale.type !== 'consul') return false
                if (this.menu.is_health_package) return false

                if (this.consultation) {
                    return this.consultation.plan.is_pay_consultation_price
                }
                if (this.user) {
                    return this.user.plan.is_pay_consultation_price
                }
                return false
            },

            /**
             * 最大割引となる所持クーポンを返す
             *
             * @return {object|null}
             */
            maxDiscountCoupon: function () {
                // 相談料の決済が不要なら割引表示しない
                if (!this.isPayPrice) {
                    return null
                }
                return this.helper.maxDiscountCoupon(this.userCoupons, this.menu, this.consultation)
            },

            /**
             * 会員のタイムゾーンを返す
             *
             * @return {string}
             */
            userTimezone: function () {
                return this.user ? this.user.timezone : 'Asia/Tokyo'
            },

            /**
             * 相談終了済みの予約リストを返す（相談日時の降順でソートした直近15件まで）
             *
             * @return {array}
             */
            recentlyConsultations: function () {
                if (this.userType === 'user') {
                    return this.$store.state.userConsultations.recentlyConsultations || []
                }
                else if (this.userType === 'doctor') {
                    return this.$store.state.doctorReservation.recentlyUserConsultations || []
                }
                return []
            },

            /**
             * 再相談可能かどうか
             *
             * @see \App\Models\DoctorMenu::isRepeatable() ※同じロジックで判定しています
             */
            isRepeatable: function () {
                // 予約データがパラメータとして渡されている場合は再相談として予約されたかどうかを返す
                if (this.consultation) {
                    return this.consultation.is_repeat_reserved
                }

                // そうでない場合は再相談として予約可能かどうかを返す
                return this.helper.isRepeatable(
                    this.menu,
                    this.recentlyConsultations,
                    this.userTimezone
                )
            },

            /**
             * 再相談可能な期間の最終日を返す
             *
             * @return {moment.Moment|null}
             */
            lastRepeatReservableDate: function () {
                if (!this.isRepeatable) return null

                let result = null

                this.recentlyConsultations.forEach((consultation) => {
                    if (result !== null) return

                    // メニューと同じ相談科・医師の相談予約かどうか
                    const isSame = consultation.medical_course_id === this.menu.medical_course_id
                        && consultation.doctor_id === this.menu.doctor_id

                    if (!isSame) return

                    const days = this.menu.repeat_reservable_days || 0
                    result = this.helper.userTime(consultation.end_time, this.userTimezone).add(days, 'days').endOf('day')
                })

                return result
            },

            /**
             * 再相談時に相談料が割引されるかどうか
             *
             * @returns {boolean}
             */
            isDiscountViaRepeat: function () {
                return this.isRepeatable
                    && this.menu.repeat_consultation_price !== null
                    && this.menu.repeat_consultation_price < this.menu.consultation_price
            },

            /**
             * 再相談可能かどうかに応じて実際の相談料を返す
             *
             * @returns {number}
             */
            actualConsultationPrice: function () {
                if (this.isRepeatable && this.menu.repeat_consultation_price !== null) {
                    return this.menu.repeat_consultation_price
                }
                return this.menu.consultation_price
            },

            /**
             * 割引されるかどうかを返す
             *
             * @returns {boolean}
             */
            hasDiscount: function () {
                // 相談料の決済が不要なら割引表示しない
                if (!this.isPayPrice) {
                    return false
                }

                // 明示的に税別料金が指定されている場合
                if (this.staticSubtotal !== null) {
                    return this.staticSubtotal < this.menu.consultation_price
                }

                return this.maxDiscountCoupon !== null || this.isDiscountViaRepeat
            },

            /**
             * 割引適用後の相談料金を返す
             *
             * @returns {number}
             */
            discountedPrice: function () {
                // 明示的に税別料金が指定されている場合
                if (this.staticSubtotal !== null) {
                    return this.staticSubtotal
                }
                // 所持するクーポンの中で最も割引される結果を返す
                if (this.maxDiscountCoupon) {
                    const price = this.actualConsultationPrice - this.maxDiscountCoupon.discount
                    return price < 1 ? 0 : price
                }
                return this.actualConsultationPrice
            },
        },

        created: function () {
            if (this.withExtender) {
                this.isShowDoctorMenuDescription = false
            }
        },
    }
</script>

<style lang="scss" scoped>
.doctorMenuWrapper {
    .headLabelWrapper {
        display: flex;

        @media screen and (max-width: 1199px) {
            display: block;
        }

        @media screen and (max-width: 991px) {
            display: flex;
        }
    }

    .headLabel {
        width: 180px;
        margin-bottom: 10px;
        padding: 3px 10px;
        color: #fff;
        font-size: 12px;
        text-align: center;
        border-radius: 6px;

        @media screen and (max-width: 991px) {
            padding: 2px 10px;
        }

        &.headLabelDraft {
            width: 120px;
            background-color: #FF8E15;
        }

        &.headLabelCancel {
            width: 120px;
            background-color: #B5B5B5;
        }

        &.headLabelDraftByDoctor {
            background-color: #06C179;
        }

        &.headLabelRepeat {
            background-color: #02B8BF;
        }
    }
}

.doctorMenuMedicalCourseName {
    color: #2A2E31;
    font-size: 13px;
    font-weight: bold;
    line-height: 1.1;
}

.doctorMenuHealthPackageIcon {
    margin: 2px 0 1px;

    img {
        width: 130px;
    }
}

.doctorMenuTitle {
    color: #2A2E31;
    font-size: 22px;
    font-weight: bold;
    line-height: 1.5;
}

.doctorMenuConsultationOverview {
    color: #7D868D;
    line-height: 1.1;
    font-size: 14px;
    font-weight: bold;

    .large {
        font-size: 19px;
    }

    .discounted {
        color: #CE798A;
    }

    .doctorMenuConsultationPrice {
        @media screen and (max-width: 767px) {
            div:nth-child(1) > span:nth-child(1) {
                display: none;
            }
        }

        .doctorMenuConsultationPriceOrigin {
            margin: 5px 0 0 5px;
            text-decoration: line-through;
        }
    }
}

.doctorMenuConsultationOverviewInfo {
    color: #7D868D;
    line-height: 1.1;
    font-size: 12px;

    > div {
        margin-top: 5px;
    }

    .doctorMenuConsultationOverviewInfoCoupon {
        color: #CE798A;
        font-weight: bold;
    }
}

.doctorMenuLabel {
    display: inline-block;
    margin-right: 5px;
    margin-bottom: 5px;
    padding: 2px 12px;
    color: #53A15B;
    font-size: 13px;
    border: 1px solid #DFEDDC;
    background-color: #F3F8F2;
    border-radius: 6px;

    &:last-child {
        margin-right: 0;
    }
}

.descriptionWrapper {
    position: relative;
    height: 56px;
    overflow: hidden;
    margin-top: 15px;
    padding: 15px;
    background-color: #F9F9F9;
    border: 1px solid #EDEDED;
    border-radius: 6px 6px 0 0;
    border-bottom: none;

    &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -5px;
        width: 100%;
        height: 5px;
        background: #fff;
        box-shadow: 0 -3px 4px #fff;
    }

    &.descriptionWrapperExtended {
        height: auto;
        border-radius: 6px;
        border-bottom: 1px solid #EDEDED;

        &::after {
            display: none;
        }
    }
}

.description {
    overflow: hidden;
    color: #757C83;
    font-size: 15px;
    line-height: 24px;
    white-space: pre-wrap;
}

.descriptionExtender {
    position: relative;
    left: -2px;
    margin-top: 6px;
    color: #7D868D;
    font-size: 15px;
    text-align: center;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        text-decoration: none;
    }
}
</style>
